/**
 * Returns the first bookmark anchored within the currently visible page, or null.
 *
 * In a paginated EPUB a visible "page" is a *range* of content, not a single point, so a
 * bookmark belongs to the current page when its progression falls inside the visible
 * progression range of its resource — not when it equals the first visible position. That
 * avoids false positives when one position spans several pages (the bookmark is on a
 * different page of the same position), and it tolerates the first visible position shifting
 * while the bookmarked spot is still on screen.
 *
 * It does NOT survive a reflow. Progression is layout-derived, so after a font, margin or
 * viewport change the stored fraction and the freshly computed visible range are in
 * different frames of reference, and a bookmark whose content is on screen can fail to
 * match. Worst case is a duplicate bookmark, not data loss. The locator also carries
 * `cssSelector` (see `buildBookmarkAtCurrentLocation`); matching a resolved, on-screen
 * selector first and falling back to progression would close this.
 *
 * @param {Array<Object>} bookmarks - bookmark selections, each carrying a locator
 * @param {{ progressions: Map<string, { start: number, end: number }> }} [viewport]
 *   the EpubNavigator viewport (resource href → visible progression range)
 * @returns {Object|null} the matching bookmark, or null
 */
export default function matchBookmarkInViewport(bookmarks = [], viewport) {
    if (!viewport || !viewport.progressions) return null;

    return (
        bookmarks.find(bookmark => {
            const location = bookmark.locator;
            if (!location || !location.href) return false;

            const range = viewport.progressions.get(location.href);
            if (!range) return false;

            const progression = location.locations?.progression;
            return progression != null && progression >= range.start && progression <= range.end;
        }) ?? null
    );
}
